import { createContext, useCallback, useContext, useState } from "react";
import { api } from "./api";

const dict = {
  en: {
    "nav.features": "Features", "nav.pricing": "Pricing", "nav.docs": "Docs", "nav.support": "Support",
    "desc.messages": "Every message submitted by this workspace, with its provider response and event history.",
    "desc.webhooks": "Signed HTTPS callbacks for email events, with retries and replay.",
    "desc.templates": "Reusable HTML and text templates with safe variables.",
    "desc.files": "Attachments for your emails and images for your templates, stored securely for this workspace.",
    "desc.suppressions": "Addresses that will never be sent to: hard bounces, complaints, unsubscribes and manual entries.",
    "desc.usage": "Live (non-test) messages counted against your workspace limits.",
    "desc.audit": "Key, domain, role, webhook and sending actions in this workspace.",
    "desc.team": "Owner, Admin, Developer and Read-only roles are enforced on the server for every request.",
    "desc.docs": "REST API reference with copyable examples.",
    "desc.domains": "You can only send from domains that AWS has verified for this workspace.",
    "desc.apikeys": "Keys are shown once and stored only as a secure hash.",
    "tpl.new": "New template",
    "tpl.select": "Select or create a template",
    "tpl.none": "No templates yet.",
    "tpl.name": "Name",
    "tpl.subject": "Subject",
    "tpl.text": "Plain text",
    "tpl.saved": "Template saved",
    "tpl.vars": "Values are HTML-escaped when rendered.",
    "tpl.use": "Use",
    "tpl.savefirst": "Save the template to preview it.",
    "tpl.testsend": "Send a real test message",
    "tpl.sendtest": "Send test",
    "tpl.insert": "Insert image",
    "files.tab": "Files",
    "files.bin": "Deleted · 30 days",
    "files.bininfo": "Deleted files stay here for 30 days. After that they are erased for good and can't be restored.",
    "files.restore": "Restore",
    "files.daysleft": "days left",
    "files.binempty": "The bin is empty",
    "files.binemptybody": "Files you delete appear here for 30 days so an admin can restore them.",
    "files.none": "No files yet",
    "files.nonebody": "Upload an attachment or a template image to get started.",
    "files.att": "Attachment",
    "files.img": "Template image",
    "files.atthelp": "Any document up to 10 MB. Never public; only sent with your emails.",
    "files.imghelp": "PNG, JPEG, WebP or GIF up to 5 MB. Gets a public link to use inside emails.",
    "files.choose": "Choose file",
    "dash.ready": "Ready to send",
    "desc.billing": "Your plan sets this workspace's sending limits. Payments are processed securely by Stripe.",
    "desc.settings": "Name, language, logo, usage warnings and how this workspace sends.",
    "role.owner": "owner",
    "role.admin": "admin",
    "role.developer": "developer",
    "role.read_only": "read-only",
    "kind.attachment": "attachment",
    "kind.image": "image",
    "kind.logo": "logo",
    "tab.dashboard": "Overview", "tab.messages": "Messages", "tab.templates": "Templates", "tab.domains": "Domains",
    "nav.more": "More", "pwa.title": "Install CARIO Mail", "pwa.body": "Add the app to your home screen", "pwa.install": "Install",
    "pwa.ios": "Add CARIO Mail to your home screen for a full-screen app.", "pwa.ios1": "Tap the Share button in Safari", "pwa.ios2": "Choose 'Add to Home Screen'",
    "nav.signin": "Sign in", "nav.signup": "Get started", "nav.dashboard": "Dashboard", "nav.messages": "Messages",
    "nav.templates": "Templates", "nav.files": "Files", "nav.domains": "Domains", "nav.suppressions": "Suppressions", "nav.apikeys": "API keys",
    "nav.webhooks": "Webhooks", "nav.usage": "Usage", "nav.team": "Team", "nav.audit": "Audit log", "nav.settings": "Settings",
    "nav.admin": "Platform admin", "nav.billing": "Plan & billing", "nav.signout": "Sign out", "nav.group.sending": "Sending", "nav.group.dev": "Developers",
    "nav.group.workspace": "Workspace", "nav.group.platform": "Platform", "nav.group.marketing": "Marketing", "nav.campaigns": "Campaigns", "nav.audiences": "Contacts", "nav.guides": "Guides", "nav.blog": "Blog",
    "hero.eyebrow": "Transactional email infrastructure",
    "hero.title": "Email delivery your product can rely on — with every status told honestly.",
    "hero.body": "Send transactional email through a clean REST API backed by Amazon SES. Verified sender domains, hashed API keys, signed webhooks, suppression lists and audit trails are built in.",
    "hero.cta": "Create a workspace", "hero.docs": "Read the docs",
    "auth.email": "Email", "auth.password": "Password", "auth.name": "Your name", "auth.workspace": "Workspace name (optional)",
    "auth.signin": "Sign in", "auth.signup": "Create account", "auth.google": "Continue with Google", "auth.forgot": "Forgot password?",
    "auth.noaccount": "No account yet?", "auth.haveaccount": "Already have an account?", "auth.reset": "Reset password",
    "auth.sendreset": "Send reset link", "auth.newpassword": "New password", "auth.or": "or",
    "common.save": "Save", "common.cancel": "Cancel", "common.delete": "Delete", "common.create": "Create", "common.copy": "Copy",
    "common.copied": "Copied", "common.loading": "Loading", "common.refresh": "Refresh", "common.unavailable": "Unavailable",
    "dash.title": "Overview", "dash.desc": "Sending activity, configuration and limits for this workspace.",
    "m.sent": "Submitted", "m.accepted": "Accepted", "m.delivered": "Delivered", "m.bounced": "Bounced",
    "m.complained": "Complained", "m.failed": "Failed",
    "notconfigured": "Email sending is not configured",
  },
  fi: {
    "nav.features": "Ominaisuudet", "nav.pricing": "Hinnoittelu", "nav.docs": "Dokumentaatio", "nav.support": "Tuki",
    "desc.messages": "Jokainen työtilan lähettämä viesti palvelimen vastauksineen ja tapahtumahistorioineen.",
    "desc.webhooks": "Allekirjoitetut HTTPS-kutsut sähköpostitapahtumista, uudelleenyrityksin ja toistolla.",
    "desc.templates": "Uudelleenkäytettävät HTML- ja tekstipohjat turvallisilla muuttujilla.",
    "desc.files": "Liitteet sähköposteihisi ja kuvat mallipohjiisi, tallennettuna turvallisesti tähän työtilaan.",
    "desc.suppressions": "Osoitteet, joihin ei koskaan lähetetä: pysyvät palautukset, valitukset, tilauksen peruutukset ja käsin lisätyt.",
    "desc.usage": "Oikeat (ei testi) viestit, jotka lasketaan työtilan rajoihin.",
    "desc.audit": "Avain-, verkkotunnus-, rooli-, webhook- ja lähetystapahtumat tässä työtilassa.",
    "desc.team": "Omistaja-, ylläpitäjä-, kehittäjä- ja lukuoikeusroolit tarkistetaan palvelimella jokaisessa pyynnössä.",
    "desc.docs": "REST API -ohjeet kopioitavine esimerkkeineen.",
    "desc.domains": "Voit lähettää vain verkkotunnuksista, jotka AWS on vahvistanut tälle työtilalle.",
    "desc.apikeys": "Avaimet näytetään vain kerran ja tallennetaan vain turvallisena tiivisteenä.",
    "tpl.new": "Uusi mallipohja",
    "tpl.select": "Valitse tai luo mallipohja",
    "tpl.none": "Ei vielä mallipohjia.",
    "tpl.name": "Nimi",
    "tpl.subject": "Aihe",
    "tpl.text": "Pelkkä teksti",
    "tpl.saved": "Mallipohja tallennettu",
    "tpl.vars": "Arvot suojataan HTML:ää varten, kun viesti muodostetaan.",
    "tpl.use": "Käytä",
    "tpl.savefirst": "Tallenna mallipohja nähdäksesi esikatselun.",
    "tpl.testsend": "Lähetä oikea testiviesti",
    "tpl.sendtest": "Lähetä testi",
    "tpl.insert": "Lisää kuva",
    "files.tab": "Tiedostot",
    "files.bin": "Poistetut · 30 päivää",
    "files.bininfo": "Poistetut tiedostot säilyvät täällä 30 päivää. Sen jälkeen ne poistetaan pysyvästi eikä niitä voi palauttaa.",
    "files.restore": "Palauta",
    "files.daysleft": "päivää jäljellä",
    "files.binempty": "Roskakori on tyhjä",
    "files.binemptybody": "Poistamasi tiedostot näkyvät täällä 30 päivää, jotta ylläpitäjä voi palauttaa ne.",
    "files.none": "Ei vielä tiedostoja",
    "files.nonebody": "Lataa liite tai mallipohjan kuva aloittaaksesi.",
    "files.att": "Liite",
    "files.img": "Mallipohjan kuva",
    "files.atthelp": "Mikä tahansa asiakirja, enintään 10 Mt. Ei koskaan julkinen; lähetetään vain sähköpostiesi mukana.",
    "files.imghelp": "PNG, JPEG, WebP tai GIF, enintään 5 Mt. Saa julkisen linkin sähköposteissa käytettäväksi.",
    "files.choose": "Valitse tiedosto",
    "dash.ready": "Valmis lähettämään",
    "Test mode — no email was sent": "Testitila — viestiä ei lähetetty",
    "Provider response": "Palvelimen vastaus",
    "Events": "Tapahtumat",
    "Status": "Tila",
    "From": "Lähettäjä",
    "To": "Vastaanottaja",
    "recipient(s)": "vastaanottajaa",
    "Suppressed": "Estetyt",
    "Source": "Lähde",
    "Attempts": "Lähetysyritykset",
    "Created": "Luotu",
    "Simulated": "Simuloitu",
    "Search subject, recipient or message ID": "Hae aiheella, vastaanottajalla tai viestin tunnuksella",
    "No messages found": "Viestejä ei löytynyt",
    "Send your first email with the REST API or a template test send.": "Lähetä ensimmäinen viestisi REST API:lla tai mallipohjan testilähetyksellä.",
    "API: attach a file with": "API: liitä tiedosto näin",
    "Copy link": "Kopioi linkki",
    "File moved to the bin for 30 days": "Tiedosto siirretty roskakoriin 30 päiväksi",
    "deleted": "poistettu",
    "kind.attachment": "liite",
    "kind.image": "kuva",
    "kind.logo": "logo",
    "or send it inline as base64. See Docs.": "tai lähetä se base64-muodossa. Katso ohjeet.",
    "Current plan": "Nykyinen paketti",
    "Stripe is in test mode: use card 4242 4242 4242 4242. No real money moves.": "Stripe on testitilassa: käytä korttia 4242 4242 4242 4242. Oikeaa rahaa ei siirry.",
    "Payment history": "Maksuhistoria",
    "Invoices": "Laskut",
    "No invoices yet. They appear here after your first payment.": "Ei vielä laskuja. Ne näkyvät täällä ensimmäisen maksun jälkeen.",
    "Unlimited": "Rajaton",
    "Monthly": "Kuukausi",
    "Daily": "Päivä",
    "Per minute": "Minuutissa",
    "Workspace name": "Työtilan nimi",
    "Sending configuration": "Lähetysasetukset",
    "Workspace logo": "Työtilan logo",
    "Shown in the app sidebar and in the header of invite, usage and receipt emails for this workspace. PNG, JPEG or WebP, up to 2 MB.": "Näkyy sovelluksen sivupalkissa sekä kutsu-, käyttö- ja kuittiviestien ylälaidassa. PNG, JPEG tai WebP, enintään 2 Mt.",
    "Remove": "Poista",
    "Only admins and owners can change the logo.": "Vain ylläpitäjät ja omistajat voivat vaihtaa logon.",
    "Usage warning emails": "Käyttövaroitukset sähköpostiin",
    "Sent once a month at 80% and at 100% of the monthly plan limit. Owners always receive them.": "Lähetetään kerran kuussa, kun 80 % ja 100 % kuukausirajasta täyttyy. Omistajat saavat ne aina.",
    "Only owners can change this.": "Vain omistajat voivat muuttaa tätä.",
    "Your role": "Roolisi",
    "Signed in as": "Kirjautuneena",
    "Also send to admins": "Lähetä myös ylläpitäjille",
    "role.owner": "omistaja",
    "role.admin": "ylläpitäjä",
    "role.developer": "kehittäjä",
    "role.read_only": "lukuoikeus",
    "desc.billing": "Paketti määrää työtilan lähetysrajat. Maksut käsittelee turvallisesti Stripe.",
    "desc.settings": "Nimi, kieli, logo, käyttövaroitukset ja työtilan lähetystapa.",
    "Messages are relayed through": "Viestit välitetään palvelun kautta:",
    "Why emails bounced": "Miksi viestit palautuivat",
    "Bounces and rejections in this range, grouped by reason. Open a bounced message for a plain-language explanation.": "Palautukset ja hylkäykset tällä aikavälillä syittäin ryhmiteltynä. Avaa palautunut viesti, niin saat selityksen selkokielellä.",
    "Results by tag": "Tulokset tunnisteittain",
    "Tag emails with e.g.": "Merkitse viestit esim.",
    "Tag": "Tunniste",
    "Delivery rate": "Toimitusaste",
    "\"Accepted\" means the upstream SMTP server accepted the message — not confirmed inbox delivery.": "\"Hyväksytty\" tarkoittaa, että SMTP-palvelin otti viestin vastaan — toimitusta postilaatikkoon ei ole vahvistettu.",
    "Submitted messages": "Lähetetyt viestit",
    "Usage limits": "Käyttörajat",
    "Add a sending domain →": "Lisää lähettävä verkkotunnus →",
    "Recent messages": "Viimeisimmät viestit",
    "All →": "Kaikki →",
    "No messages yet.": "Ei vielä viestejä.",
    "Recent activity": "Viimeisin toiminta",
    "No activity yet.": "Ei vielä toimintaa.",
    "No live messages in this range yet.": "Tällä aikavälillä ei vielä oikeita viestejä.",
    "test-mode message(s) excluded.": "testiviestiä jätetty pois.",
    "Today": "Tänään",
    "This month": "Tässä kuussa",
    "Rate limit": "Nopeusraja",
    "unlimited (super admin)": "rajaton (pääylläpitäjä)",
    "minute": "minuutti",
    "tab.dashboard": "Koti", "tab.messages": "Viestit", "tab.templates": "Pohjat", "tab.domains": "Tunnukset",
    "Uploading": "Ladataan", "Upload": "Lataa",
    "/ month": "/ kk",
    "Most popular": "Suosituin",
    "Start free": "Aloita ilmaiseksi",
    "Choose": "Valitse",
    "Switch to": "Vaihda hintaan",
    "Subscribe for": "Tilaa hintaan",
    "Cancel to downgrade": "Peru ja siirry Free-pakettiin",
    "Downgrade scheduled": "Siirto Free-pakettiin ajastettu",
    "Updating…": "Päivitetään…",
    "Redirecting…": "Siirrytään…",
    "Pay for exactly the volume you need": "Maksa juuri tarvitsemastasi määrästä",
    "emails / month": "viestiä / kk",
    "1,000 emails / month": "1 000 viestiä / kk",
    "200 / day · 10 / minute": "200 / päivä · 10 / minuutti",
    "REST API, templates, webhooks": "REST API, mallipohjat, webhookit",
    "Verified sender domains": "Vahvistetut lähettäjäverkkotunnukset",
    "50,000 emails / month": "50 000 viestiä / kk",
    "10,000 / day · 60 / minute": "10 000 / päivä · 60 / minuutti",
    "Delivery events & tag analytics": "Toimitustapahtumat ja tunnisteanalytiikka",
    "Team roles & audit log": "Tiimiroolit ja tapahtumaloki",
    "250,000 emails / month": "250 000 viestiä / kk",
    "50,000 / day · 200 / minute": "50 000 / päivä · 200 / minuutti",
    "Everything in Pro": "Kaikki Pro-paketin ominaisuudet",
    "Priority review of limits": "Rajojen priorisoitu käsittely",
    "€1.00 per 1,000 emails at 10k, sliding down to €0.50 per 1,000 from 1M emails a month.": "1,00 € / 1 000 viestiä 10 000 viestin kohdalla, laskien 0,50 €:oon / 1 000 kun viestejä on yli miljoona kuukaudessa.",
    "per month": "kuukaudessa",
    "Only workspace owners can change the plan": "Vain työtilan omistajat voivat vaihtaa pakettia",
    "Change your subscription now? Stripe charges or credits the prorated difference immediately.": "Vaihdetaanko tilaus nyt? Stripe veloittaa tai hyvittää suhteutetun erotuksen heti.",
    "Plan changed": "Paketti vaihdettu",
    "Cancel your subscription? You keep your plan until the end of the paid period, then return to Free.": "Perutaanko tilaus? Paketti on käytössä maksetun jakson loppuun, minkä jälkeen palaat Free-pakettiin.",
    "Subscription will end at the period end": "Tilaus päättyy jakson lopussa",
    "nav.more": "Lisää", "pwa.title": "Asenna CARIO Mail", "pwa.body": "Lisää sovellus kotinäytölle", "pwa.install": "Asenna",
    "pwa.ios": "Lisää CARIO Mail kotinäytölle, niin se aukeaa koko näytön sovelluksena.", "pwa.ios1": "Napauta Safarin Jaa-painiketta", "pwa.ios2": "Valitse 'Lisää Koti-valikkoon'",
    "nav.signin": "Kirjaudu", "nav.signup": "Aloita", "nav.dashboard": "Yleiskatsaus", "nav.messages": "Viestit",
    "nav.templates": "Mallipohjat", "nav.files": "Tiedostot", "nav.domains": "Verkkotunnukset", "nav.suppressions": "Estolista", "nav.apikeys": "API-avaimet",
    "nav.webhooks": "Webhookit", "nav.usage": "Käyttö", "nav.team": "Tiimi", "nav.audit": "Tapahtumaloki", "nav.settings": "Asetukset",
    "nav.admin": "Alustan hallinta", "nav.billing": "Paketti ja laskutus", "nav.signout": "Kirjaudu ulos", "nav.group.sending": "Lähetys", "nav.group.dev": "Kehittäjät",
    "nav.group.workspace": "Työtila", "nav.group.platform": "Alusta", "nav.group.marketing": "Markkinointi", "nav.campaigns": "Kampanjat", "nav.audiences": "Yhteystiedot", "nav.guides": "Oppaat", "nav.blog": "Blogi",
    "hero.eyebrow": "Transaktiosähköpostin infrastruktuuri",
    "hero.title": "Sähköpostin toimitus, johon tuotteesi voi luottaa — ja jokainen tila kerrotaan rehellisesti.",
    "hero.body": "Lähetä transaktioviestejä selkeän REST-rajapinnan kautta Amazon SES:n avulla. Varmennetut lähettäjäverkkotunnukset, tiivistetyt API-avaimet, allekirjoitetut webhookit, estolistat ja tapahtumalokit ovat valmiina.",
    "hero.cta": "Luo työtila", "hero.docs": "Lue dokumentaatio",
    "auth.email": "Sähköposti", "auth.password": "Salasana", "auth.name": "Nimesi", "auth.workspace": "Työtilan nimi (valinnainen)",
    "auth.signin": "Kirjaudu sisään", "auth.signup": "Luo tili", "auth.google": "Jatka Googlella", "auth.forgot": "Unohtuiko salasana?",
    "auth.noaccount": "Eikö sinulla ole tiliä?", "auth.haveaccount": "Onko sinulla jo tili?", "auth.reset": "Vaihda salasana",
    "auth.sendreset": "Lähetä palautuslinkki", "auth.newpassword": "Uusi salasana", "auth.or": "tai",
    "common.save": "Tallenna", "common.cancel": "Peruuta", "common.delete": "Poista", "common.create": "Luo", "common.copy": "Kopioi",
    "common.copied": "Kopioitu", "common.loading": "Ladataan", "common.refresh": "Päivitä", "common.unavailable": "Ei saatavilla",
    "Start your first campaign \u2192": "Aloita ensimmäinen kampanjasi \u2192", "dash.title": "Yleiskatsaus", "dash.desc": "Työtilan lähetykset, asetukset ja rajat.",
    "m.sent": "Lähetetty", "m.accepted": "Hyväksytty", "m.delivered": "Toimitettu", "m.bounced": "Palautunut",
    "m.complained": "Valitus", "m.failed": "Epäonnistunut",
    "notconfigured": "Sähköpostin lähetystä ei ole määritetty",
  },
};

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const l = localStorage.getItem("cario_lang") || "en";
    document.documentElement.lang = l;
    return l;
  });
  const setLang = (l) => {
    localStorage.setItem("cario_lang", l); document.documentElement.lang = l; setLangState(l);
    api.patch("/auth/me/lang", { lang: l }).catch((e) => console.warn("Could not save language preference", e));
  };
  const syncLang = useCallback((l) => {
    if (!dict[l] || l === localStorage.getItem("cario_lang")) return;
    localStorage.setItem("cario_lang", l); document.documentElement.lang = l; setLangState(l);
  }, []);
  const t = (k) => dict[lang]?.[k] ?? dict.en[k] ?? k;
  return <I18nCtx.Provider value={{ lang, setLang, syncLang, t }}>{children}</I18nCtx.Provider>;
}

export const useT = () => useContext(I18nCtx);
